import type { CSSProperties } from "react";
import type { ReviewSourceElementProps } from "./types";
import { resolveDynamicColor, normalizePadding, mapTextStyle } from "./utils";

export interface ReviewSourceElementComponentProps extends ReviewSourceElementProps {
  /** Additional CSS class name */
  className?: string;
  /** Container style overrides */
  containerStyle?: CSSProperties;
}

/**
 * ReviewSourceElement - Renders review source as either text name or logo.
 * Handles both icon components and image assets with consistent color resolution.
 */
export function ReviewSourceElement({
  display,
  sourceName,
  logoUrl,
  IconComponent,
  lockIconColor = false,
  brandColors,
  templateDefaults,
  style,
  size,
  opacity,
  borderRadius,
  borderWidth,
  borderColor,
  shadow,
  backgroundBlendMode,
  className,
  containerStyle,
}: ReviewSourceElementComponentProps) {
  // Resolve colors
  const resolvedTextColor = style?.color
    ? resolveDynamicColor(style.color, templateDefaults, brandColors, brandColors.primaryText)
    : brandColors.primaryText;
  
  const resolvedBackgroundColor = style?.backgroundColor
    ? resolveDynamicColor(style.backgroundColor, templateDefaults, brandColors, style.backgroundColor)
    : undefined;
  
  const resolvedBorderColor = borderColor
    ? resolveDynamicColor(borderColor, templateDefaults, brandColors, borderColor)
    : style?.borderColor
      ? resolveDynamicColor(style.borderColor, templateDefaults, brandColors, style.borderColor)
      : undefined;
  
  const padding = normalizePadding(style);
  
  // Determine justification based on display mode and text alignment
  const justifyContent = (() => {
    if (display === "logo") return "center";
    const align = style?.textAlign ?? "left";
    if (align === "center") return "center";
    if (align === "right") return "flex-end";
    return "flex-start";
  })();
  
  const containerCss: CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent,
    backgroundColor: resolvedBackgroundColor,
    borderRadius: borderRadius ?? style?.borderRadius ? `${borderRadius ?? style?.borderRadius}px` : undefined,
    borderWidth: borderWidth ?? style?.borderWidth ? `${borderWidth ?? style?.borderWidth}px` : undefined,
    borderColor: resolvedBorderColor,
    borderStyle: (borderWidth ?? style?.borderWidth) ? "solid" : undefined,
    boxShadow: shadow ?? style?.shadow ?? undefined,
    padding,
    opacity: opacity ?? style?.opacity ?? 1,
    mixBlendMode: (backgroundBlendMode ?? style?.backgroundBlendMode) as CSSProperties["mixBlendMode"] | undefined,
    ...containerStyle,
  };
  
  // Logo display mode
  if (display === "logo") {
    // Use element size dimensions for logo sizing
    const targetWidth = typeof size?.width === "number" ? `${size.width}px` : undefined;
    const targetHeight = typeof size?.height === "number" ? `${size.height}px` : undefined;
    
    // Icon component
    if (IconComponent) {
      const iconStyle: CSSProperties = {
        width: targetWidth ?? "100%",
        height: targetHeight ?? "100%",
        maxWidth: "100%",
        maxHeight: "100%",
      };
      
      if (!lockIconColor) {
        iconStyle.color = resolvedTextColor;
      }
      
      return (
        <div className={className} style={containerCss}>
          <IconComponent
            className="inline-flex items-center justify-center"
            style={iconStyle}
            title={`${sourceName} logo`}
          />
        </div>
      );
    }
    
    // Image asset
    if (logoUrl) {
      const imageStyle: CSSProperties = {
        width: targetWidth ?? "100%",
        height: targetHeight ?? "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
        display: "block",
      };
      
      return (
        <div className={className} style={containerCss}>
          <img src={logoUrl} alt={`${sourceName} logo`} style={imageStyle} />
        </div>
      );
    }
    
    // Fallback to text if no logo available
    const textStyle = mapTextStyle(style, brandColors, templateDefaults, {
      color: resolvedTextColor,
    });
    
    return (
      <div className={className} style={containerCss}>
        <span style={textStyle}>{sourceName}</span>
      </div>
    );
  }
  
  // Name display mode
  const textStyle = mapTextStyle(style, brandColors, templateDefaults);
  
  return (
    <div className={className} style={containerCss}>
      <span style={textStyle}>{sourceName}</span>
    </div>
  );
}
